import React, { useEffect, useState, useMemo } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { LayoutDashboard, Users, Palette, LogOut, Shield, HardDrive, Globe, ShieldCheck, Mail, CreditCard, Wallet, Crown, UserCheck, UserCircle, Package, AlertTriangle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const navItems = [
  { to: "/admin/dashboard", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/admin/clients", icon: Users, label: "Clients", perm: "clients" },
  { to: "/admin/subscriptions", icon: CreditCard, label: "Pagos", perm: "subscriptions" },
  { to: "/admin/payment-providers", icon: Wallet, label: "Pasarelas de pago", perm: "payments" },
  { to: "/admin/reseller-plans", icon: Package, label: "Planes Reseller", rootOnly: true },
  { to: "/admin/reseller-subscriptions", icon: CreditCard, label: "Pagos Reseller", rootOnly: true },
  { to: "/admin/super-admins", icon: ShieldCheck, label: "Super Admins", rootOnly: true },
  { to: "/admin/landing", icon: Globe, label: "Landing Page", perm: "landing" },
  { to: "/admin/emails", icon: Mail, label: "Emails", perm: "emails" },
  { to: "/admin/branding", icon: Palette, label: "Branding", perm: "branding" },
  { to: "/admin/maintenance", icon: HardDrive, label: "Maintenance", rootOnly: true },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const impersonating = !!localStorage.getItem("wa_admin_root_token");

  useEffect(() => {
    const token = localStorage.getItem("wa_admin_token");
    if (!token) { navigate("/admin/login"); return; }
    const stored = localStorage.getItem("wa_admin_user");
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.role !== "super_admin") { navigate("/admin/login"); return; }
      setAdmin(parsed);
    }
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        if (data.role !== "super_admin") {
          localStorage.removeItem("wa_admin_token");
          localStorage.removeItem("wa_admin_user");
          navigate("/admin/login");
          return;
        }
        setAdmin(data);
        localStorage.setItem("wa_admin_user", JSON.stringify(data));
      })
      .catch(() => {
        localStorage.removeItem("wa_admin_token");
        localStorage.removeItem("wa_admin_user");
        navigate("/admin/login");
      });
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("wa_admin_token");
    localStorage.removeItem("wa_admin_user");
    localStorage.removeItem("wa_admin_root_token");
    localStorage.removeItem("wa_admin_root_user");
    navigate("/admin/login");
  };

  const stopImpersonating = () => {
    const rootToken = localStorage.getItem("wa_admin_root_token");
    const rootUser = localStorage.getItem("wa_admin_root_user");
    if (rootToken && rootUser) {
      localStorage.setItem("wa_admin_token", rootToken);
      localStorage.setItem("wa_admin_user", rootUser);
      localStorage.removeItem("wa_admin_root_token");
      localStorage.removeItem("wa_admin_root_user");
      window.location.href = "/admin/dashboard";
    } else {
      handleLogout();
    }
  };

  const getAdminAxios = () => {
    const token = localStorage.getItem("wa_admin_token");
    return axios.create({ headers: { Authorization: `Bearer ${token}` } });
  };

  const isRoot = !!(admin && admin.is_root_super_admin);
  const perms = (admin && admin.sa_perms) || {};
  const branding = (admin && admin.sa_branding) || {};
  const billing = (admin && admin.sa_billing) || null;

  // Apply sub-admin's accent color as a CSS variable (used for the highlight tone)
  useEffect(() => {
    if (!admin) return;
    if (!isRoot && branding.accent_color) {
      document.documentElement.style.setProperty("--sa-accent", branding.accent_color);
    } else {
      document.documentElement.style.removeProperty("--sa-accent");
    }
  }, [admin, isRoot, branding.accent_color]);

  const visibleItems = useMemo(() => navItems.filter((item) => {
    if (item.rootOnly) return isRoot;
    if (!item.perm) return true;
    if (isRoot) return true;
    return !!perms[item.perm];
  }), [isRoot, perms]);

  if (!admin) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0c0a1d]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-400" />
      </div>
    );
  }

  const showBillingBanner = !isRoot && billing && (billing.status === "expiring" || billing.status === "expired" || billing.status === "none");
  const brandName = (!isRoot && branding.brand_name) ? branding.brand_name : "Admin Panel";
  const brandLogo = (!isRoot && branding.logo_url) ? branding.logo_url : null;
  const accent = (!isRoot && branding.accent_color) ? branding.accent_color : null;

  return (
    <div className="flex h-screen bg-[#0c0a1d]" data-testid="admin-layout">
      <aside className="w-64 bg-[#13112b] text-white flex flex-col shrink-0 border-r border-indigo-900/30" data-testid="admin-sidebar">
        <div className="p-5 flex items-center gap-3">
          {brandLogo ? (
            <img src={brandLogo} alt={brandName} className="w-9 h-9 rounded-lg object-cover" />
          ) : (
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: accent ? `${accent}33` : "rgba(245, 158, 11, 0.2)" }}>
              {isRoot ? <Crown className="w-5 h-5 text-amber-400" /> : <Shield className="w-5 h-5" style={{ color: accent || "#f59e0b" }} />}
            </div>
          )}
          <div className="min-w-0">
            <span className="font-semibold text-base tracking-tight block truncate" data-testid="admin-brand-name">{brandName}</span>
            {isRoot && (
              <span className="block text-[10px] text-amber-400 font-medium uppercase tracking-widest">Root Super Admin</span>
            )}
          </div>
        </div>
        <Separator className="bg-white/5" />
        <nav className="flex-1 py-3 px-3 space-y-1 overflow-y-auto" data-testid="admin-nav">
          {visibleItems.map((item) => (
            <NavLink
              key={item.to} to={item.to} end={item.end}
              data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-amber-500/10 text-amber-400 font-medium" : "text-slate-400 hover:text-white hover:bg-white/5"
                }`
              }
              style={({ isActive }) => isActive && accent ? { backgroundColor: `${accent}1a`, color: accent } : undefined}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
          {/* Mi cuenta: always visible for ALL super admins (including root) */}
          <NavLink
            to="/admin/me"
            data-testid="admin-nav-mi-cuenta"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isActive ? "bg-amber-500/10 text-amber-400 font-medium" : "text-slate-400 hover:text-white hover:bg-white/5"
              }`
            }
            style={({ isActive }) => isActive && accent ? { backgroundColor: `${accent}1a`, color: accent } : undefined}
          >
            <UserCircle className="w-4 h-4" />
            Mi cuenta
          </NavLink>
          {/* Billing: only sub admins */}
          {!isRoot && (
            <NavLink
              to="/admin/billing"
              data-testid="admin-nav-facturacion"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-amber-500/10 text-amber-400 font-medium" : "text-slate-400 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <CreditCard className="w-4 h-4" />
              Facturación
            </NavLink>
          )}
        </nav>
        <div className="p-3 border-t border-white/5">
          <div className="px-3 py-2 mb-2">
            <p className="text-xs text-slate-500">Signed in as</p>
            <p className="text-sm font-medium text-white truncate" data-testid="admin-user-email">{admin.name || admin.email}</p>
            {!isRoot && billing && billing.expires_on && (
              <p className="text-[10px] text-slate-500 mt-0.5" data-testid="admin-plan-mini">
                {billing.plan_name} · vence {billing.expires_on.slice(0, 10)}
              </p>
            )}
          </div>
          <Button variant="ghost" className="w-full justify-start text-slate-400 hover:text-white hover:bg-white/5" onClick={handleLogout} data-testid="admin-logout-button">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto">
        {impersonating && (
          <div className="sticky top-0 z-30 bg-amber-500 text-black px-4 py-2 text-sm flex items-center justify-between gap-3" data-testid="impersonation-banner">
            <span className="flex items-center gap-2">
              <UserCheck className="w-4 h-4" />
              <strong>Viendo como {admin.email}</strong> · perspectiva del sub admin.
            </span>
            <Button size="sm" variant="outline" onClick={stopImpersonating} className="border-black/30 text-black hover:bg-black/10 h-7" data-testid="stop-impersonating-btn">
              Volver a mi cuenta
            </Button>
          </div>
        )}
        {showBillingBanner && (
          <div
            className={`sticky top-0 z-20 px-4 py-2 text-sm flex items-center justify-between gap-3 ${
              billing.status === "expired" ? "bg-red-500 text-white" : "bg-amber-400 text-black"
            }`}
            data-testid="billing-banner"
          >
            <span className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              {billing.status === "expired"
                ? <strong>Tu plan venció. No podés crear ni editar clientes hasta renovar.</strong>
                : billing.status === "none"
                  ? <strong>Tu cuenta no tiene plan activo. Elegí uno para empezar a operar.</strong>
                  : <strong>Tu plan vence en {billing.days_left} {billing.days_left === 1 ? "día" : "días"}. Renová para no perder acceso.</strong>
              }
            </span>
            <Button size="sm" variant="outline" onClick={() => navigate("/admin/billing")} className={billing.status === "expired" ? "border-white/30 text-white hover:bg-white/10 h-7" : "border-black/30 text-black hover:bg-black/10 h-7"} data-testid="billing-banner-cta">
              Ir a Facturación
            </Button>
          </div>
        )}
        <Outlet context={{ admin, getAdminAxios }} />
      </main>
    </div>
  );
}
